import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import { toast } from 'react-toastify';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { createPortal } from 'react-dom';

import axios from '../../../../../axios-instance';

import { getHeaders } from '../../../../../helpers/getHeaders';
import { EditGuestInfoModal } from './EditGuestInfoModal';
import { UploadFileModal } from './UploadFileModal';
import { isValidEmail } from '../../../../../helpers/validators';
import { GuestBulkActions } from './GuestBulkActions';
import NewLoader from '../../../../../components/NewLoader/NewLoader';
import PleaseWaitModal from '../../NewTicketsPages/BarCodes/modals/PleaseWaitModal';
import { getCustomURLs } from '../../../../../helpers/getCustomURLs';

import './EventGuestlist.sass';
import { loadFile } from '../../../../../helpers/loadFile';
import moment from 'moment';

const MySwal = withReactContent(Swal);
const ReactTableFixedColumns = withFixedColumns(ReactTable);

const initialData = {
  name: 'Add new guest...',
  email: '',
  ticketTemplateId: null,
  key: 'default',
};

export const EventGuestlist = ({ currentEvent, token }) => {
  const [guestsList, setGuestsList] = useState([]);
  const [guestData, setGuestData] = useState({});
  const [editedGuestIdx, setEditedGuestIdx] = useState(null);
  const [isShownUploadFileSwal, setShownUploadFileSwal] = useState(false);
  const [isShownEditGuestSwal, setShownEditGuestSwal] = useState(false);
  const [isValidData, setValidData] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [lastSelectedTemplate, setLastSelectedTemplate] = useState(null);

  const { API_URL } = getCustomURLs();

  const handleChangeField = (field, value) => {
    setGuestData(prev => ({
      ...prev,
      [field]: value,
    }))
  };

  const removeGuest = (idx) => {
    const newList = guestsList.filter((g, i) => i !== idx);

    setGuestsList(newList);
    setValidData(true);
  }

  const closeUploadFileModal = (e) => {
    MySwal.close();
    setShownUploadFileSwal(false);
  };

  const closeEditGuestInfoModal = (e) => {
    MySwal.close();
    setShownEditGuestSwal(false);
    setGuestData({});
    setEditedGuestIdx(null);
  };

  const saveGuestInfo = (idx) => {
    if (idx === guestsList.length) {
      setGuestsList(prev => ([...prev, guestData]));
    } else {
      setGuestsList(prev => {
        return prev.map((guest, i) => {
          if (idx === i) {
            return guestData;
          };
  
          return guest;
        })
      });
    }
    closeEditGuestInfoModal();
    setLastSelectedTemplate(guestData.ticketTemplateId);
  };

  const openUploadFileModal = () => {
    MySwal.fire({
      onOpen: () => {
        setShownUploadFileSwal(true);
      },
      onClose: closeUploadFileModal,
      customClass: "upload-file-actions",
      showConfirmButton: false,
      width: 'max-content',
    });
  };

  const openEditGuestInfoModal = (guestInfo, idx) => {
    setValidData(true);

    MySwal.fire({
      onOpen: () => {
        setShownEditGuestSwal(true);
        setEditedGuestIdx(idx);

        if (currentEvent.ticketTemplates.length === 1) {
          setGuestData({ ...guestInfo, ticketTemplateId: currentEvent.ticketTemplates[0].id });
        } else if (lastSelectedTemplate) {
          setGuestData({ ...guestInfo, ticketTemplateId: lastSelectedTemplate });
        } else {
          setGuestData(guestInfo);
        }
      },
      onClose: closeEditGuestInfoModal,
      customClass: "edit-user-modal",
      showConfirmButton: false,
      width: 'max-content',
    });
  };

  const sendGuestList = async () => {
    let isError = false;

    guestsList.forEach(({ email, ticketTemplateId }) => {
      const ticketTemplate = currentEvent.ticketTemplates && currentEvent.ticketTemplates.find(
        tt => tt.id === ticketTemplateId
      );

      if (!isValidEmail(email) || !ticketTemplate) {
        setValidData(false);
        isError = true;
  
        return;
      };
    });

    if (isError) {
      toast.error("Required fields can't be empty");
      return;
    }

    setLoading(true);

    try {
      const body = {
        eventId: currentEvent.id,
        guests: guestsList,
      };

      const { data } = await axios.post('/GuestTickets/Load', body, {
        headers: getHeaders(token),
      });

      if (data.isSuccess) {
        toast.success('Tickets are successfully sent');
      } else {
        throw new Error(data.errorMessage);
      };
    } catch (error) {
      toast.error('Tickets not sent');
    } finally {
      setLoading(false);
    }
  };

  const downloadReportHandler = () => {
    const { id, name } = currentEvent;
    const fileName =
      name.toLowerCase().replaceAll(" ", "_") + "_guestlist_report";

    MySwal.fire({
      html: <PleaseWaitModal message='Downloading file…' />,
      customClass: 'please-wait-modal',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    axios({
      method: "GET",
      url: API_URL + `/GuestTickets/${id}?asCsv=true`,
      responseType: "arraybuffer",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((response) => {
        loadFile(
          response.data,
          `${fileName}_${moment().format("MM_DD_HH_mm")}.csv`
        );
      })
      .catch((error) => console.log(error))
      .finally(() => {
        MySwal.close();
      });
  };

  useEffect(() => {
    setValidData(true);
  }, [guestsList.length])

  const getLabelStyle = () => ({
    style: {
      marginTop: '6px',
    },
  });

  const getActionStyle = (e) => ({
    style: {
      padding: "3px 15px 0",
      display: "flex",
      alignItems: "center",
    },
    onClick: (e) => {
      e.stopPropagation()
    },
  });

  const columns = [
    {
      Header: 'Full Name',
      accessor: 'name',
      resizable: false,
      getProps: getLabelStyle,
    },
    {
      Header: () => {
        return <>Email (required)</>
      },
      accessor: 'email',
      resizable: false,
      getProps: getLabelStyle,
      Cell: ({
        row: {
          _original: { email, key },
        },
      }) => {
        if (!isValidEmail(email) && !isValidData && !key) {
          return (
            <div className="guestlist__warning">
              Invalid email
            </div>
          )
        }

        return (
          <span>{email}</span>
        )
      },
    },
    {
      Header: () => {
        return <>Ticket Template (required)</>
      },
      accessor: 'ticketTemplateId',
      resizable: false,
      getProps: getLabelStyle,
      Cell: ({
        row: {
          _original: { ticketTemplateId, key },
        },
      }) => {
        const ticketTemplate = currentEvent.ticketTemplates && currentEvent.ticketTemplates.find(tt => tt.id === ticketTemplateId);

        if (!ticketTemplate && !isValidData && !key) {
          return (
            <div className="guestlist__warning">
              This field can't be empty 
            </div>
          )
        }

        return (
          <span>{ticketTemplate ? ticketTemplateId + ' - ' + ticketTemplate.ticketName : ''}</span>
        )
      },
    },
  ]

  if (currentEvent.mapId) {
    columns.push(
      {
        Header: 'Area',
        accessor: 'area',
        resizable: false,
        width: 70,
        getProps: getLabelStyle,
      },
      {
        Header: 'Side',
        accessor: 'side',
        resizable: false,
        width: 70,
        getProps: getLabelStyle,
      },
      {
        Header: 'Row',
        accessor: 'row',
        resizable: false,
        width: 70,
        getProps: getLabelStyle,
      },
      {
        Header: 'Seat',
        accessor: 'seat',
        resizable: false,
        width: 70,
        getProps: getLabelStyle,
      },
    )
  }

  columns.push(
    {
      Cell: ({ row }) => {
        if (row._original.key ) {
          return null;
        }

        return (
          <GuestBulkActions
            edit={() => openEditGuestInfoModal(row._original, row._original._index)}
            remove={() => removeGuest(row._index)}
          />
        );
      },
      width: 65,
      getProps: getActionStyle,
      fixed: 'right',
    },
  );

  if (isLoading) {
    return (
      <div className='guestlist__loader'>
        <NewLoader />
      </div>
    )
  }

  return (
    <section className="guestlist">
      <div className="guestlist__buttons">
        <button
          className='btn-primary guestlist__upload'
          onClick={openUploadFileModal}
        >
          Upload CSV
        </button>
        <button
          className='btn-primary guestlist__upload'
          onClick={downloadReportHandler}
        >
          Download Report
        </button>
      </div>

      <ReactTableFixedColumns
        className="guestlist__table"
        data={[...guestsList, initialData]}
        columns={columns}
        defaultPageSize={100000}
        showPagination={false}
        minRows={0}
        sortable={false}
        getTrProps={(state, rowInfo) => ({
          onClick: (e) => {
            if (rowInfo.original.key) {
              openEditGuestInfoModal({ ...initialData, name: '', key: null }, rowInfo.row._index);
            } else {
              openEditGuestInfoModal(rowInfo.original, rowInfo.row._index);
            }
          },
        })}
      />

      <button
        className='btn-primary guestlist__load'
        onClick={sendGuestList}
        disabled={guestsList.length === 0 || !isValidData}
      >
        Send tickets
      </button>

      {isShownEditGuestSwal &&
        createPortal(
          <EditGuestInfoModal
            guestData={guestData}
            event={currentEvent}
            handleChangeField={handleChangeField}
            saveGuestInfo={saveGuestInfo}
            editedGuestIdx={editedGuestIdx}
            closeModalHandler={closeEditGuestInfoModal}
          />,
          MySwal.getContent(),
        )}

      {isShownUploadFileSwal &&
        createPortal(
          <UploadFileModal
            currentEvent={currentEvent}
            setGuestsList={setGuestsList}
            token={token}
            closeModalHandler={closeUploadFileModal}
          />,
          MySwal.getContent(),
        )}
    </section>
  )
}

const mapStateToProps = ({ auth, events: { currentEvent } }) => ({
  auth,
  token: auth.user.token,
  currentEvent,
});

const mapDispatchToProps = (dispatch) => ({});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EventGuestlist)
);